import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberTable from '../../../components/tables/MemberTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { useUserApi } from '../../../hooks/useUserApi';
import { AuthDTO, ImpersonationDTO, MemberDTO, NoteDTO } from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { useMemberTypes } from '../../../hooks/useMemberTypeApi';
import { useMemberCategories } from '../../../hooks/useMemberCategoryApi';
import { useMemberIdTypes } from '../../../hooks/useMemberIdTypeApi';
import { useLanguages } from '../../../hooks/useLanguageApi';
import { useCountries } from '../../../hooks/useCountryApi';
import { useProvinces } from '../../../hooks/useProvinceApi';
import { useMemberStatuses } from '../../../hooks/useMemberStatusApi';
import { useMemberDisciplinaryStatuses } from '../../../hooks/useMemberDisciplinaryStatusApi';
import { useMemberTerminationStatuses } from '../../../hooks/useMemberTerminationStatusApi';
import { useMemberRegistrationTypes } from '../../../hooks/useMemberRegistrationTypeApi';
import { useMemberRegistrationSteps } from '../../../hooks/useMemberRegistrationStepApi';
import { useMemberReferralTypes } from '../../../hooks/useMemberReferralTypeApi';
import { getUserId } from '../../../utils/tokenUtils';
import { useAuthApi } from '../../../hooks/useAuthApi';
import Drawer from '../../../components/common/Drawer';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { MemberStatusConstants } from '../../../constants/MemberStatusConstants';
import { DrawerViewConstants, MemberDrawerViews } from '../../../constants/DrawerViewConstants';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import { useNoteApi } from '../../../hooks/useNoteApi';
import ViewInvoiceHistory from '../../../components/drawer/ViewInvoiceHistory';
import { usePaymentApi } from '../../../hooks/usePaymentApi';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { ChevronDown } from 'lucide-react';
import { confirmAction, showAlert } from '../../../utils/sweetAlertUtils';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import { coerceFilterBooleans } from '../../../utils/filterUtils';
import { FileViewer } from '../../../components/common/FileViewer';
import EditMandateDrawer from '../../../components/drawer/EditMandateDrawer';
import { useDebitOrderMandateApi } from '../../../hooks/useDebitOrderMandateApi';

const MemberList: React.FC = () => {
  const { edit, search, remove, archive, restoreFromArchive, restoreFromDeleted, sendProofOfPaymentLink, updateStatus, bulkArchive, bulkDelete, extract, getMandatePdf, isLoading } =
    useMemberApi();
  const { processEftPayment, isLoading: isProcessingEftPayment } = usePaymentApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();
  const { impersonate } = useImpersonation();
  const { forgotPassword } = useUserApi();
  const [items, setItems] = useState<MemberDTO[]>([]);
  const [drawerTitle, setDrawerTitle] = useState<string>();
  const [drawerView, setDrawerView] = useState<MemberDrawerViews>();
  const [drawerSize, setDrawerSize] = useState<"small" | "large">("large");
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);
  const [editingMandateId, setEditingMandateId] = useState<string | null>(null);
  const { getMandatesByMember } = useDebitOrderMandateApi();

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    clearSearchState,
  } = useSearchState('members', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const idPassportNumberFromUrl =
    new URLSearchParams(location.search).get('idPassportNumber')?.trim() ?? '';
  const hasIdPassportNumberFromUrl = Boolean(idPassportNumberFromUrl);
  const injectedIdPassportNumberRef = useRef<string>('');

  // Get status from URL and map it correctly
  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Add status mapping state
  const [statusMap, setStatusMap] = useState<{ [key: string]: string }>({});

  // Clear items when route variant changes (prevents showing stale data from previous page)
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      setItems([]); // Clear old data immediately
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Function to get the correct filters based on status
  const getFiltersFromStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'newregistration':
        return {
          memberStatusId: statusMap['newregistration'],
          isDeleted: false,
          isArchived: false,
        };
      case 'active':
        return {
          memberStatusId: statusMap['active'],
          isDeleted: false,
          isArchived: false,
        };
      case 'none':
        return {
          memberStatusId: statusMap['none'],
          isDeleted: false,
          isArchived: false,
        };
      case 'archived':
        return { isArchived: true };
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  // Function to get display name for status
  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new application':
        return 'New Application';
      case 'active':
        return 'Active';
      case 'none':
        return 'No Status';
      case 'archived':
        return 'Archived';
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Move all API hooks to the top level
  const { data: memberTypesData, isLoading: isLoadingMemberTypes } = useMemberTypes();
  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();
  const { data: memberIdTypesData, isLoading: isLoadingMemberIdTypes } = useMemberIdTypes();
  const { data: languages, isLoading: isLoadingLanguages } = useLanguages();
  const { data: countries, isLoading: isLoadingCountries } = useCountries();
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: memberStatusesData, isLoading: isLoadingMemberStatuses } = useMemberStatuses();
  const { data: disciplinaryStatusesData, isLoading: isLoadingDisciplinaryStatuses } = useMemberDisciplinaryStatuses();
  const { data: terminationStatusesData, isLoading: isLoadingTerminationStatuses } =
    useMemberTerminationStatuses();
  const { data: registrationTypesData, isLoading: isLoadingRegistrationTypes } = useMemberRegistrationTypes();
  const { data: registrationStepsData, isLoading: isLoadingRegistrationSteps } = useMemberRegistrationSteps();
  const { data: referralTypesData, isLoading: isLoadingReferralTypes } = useMemberReferralTypes();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = getFiltersFromStatus(statusFromUrl);
      const response = await search({
        filters: {
          ...coerceFilterBooleans(filters),
          ...statusFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Members:', error);
      return 0;
    }
  };

  // Modified useEffect to handle status mapping
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        // Map status IDs
        const statusMapping: { [key: string]: string } = {};
        (memberStatusesData || []).forEach((status) => {
          if (status.title?.toLowerCase() === 'new registration') {
            statusMapping['newregistration'] = status.id ?? '';
          }
          if (status.title?.toLowerCase() === 'active') {
            statusMapping['active'] = status.id ?? '';
          }
          if (status.title?.toLowerCase() === 'none') {
            statusMapping['none'] = status.id ?? '';
          }
        });
        setStatusMap(statusMapping);

        // Pre-populate search from URL query string (e.g. from Recon Transactions "View Member")
        if (idPassportNumberFromUrl) {
          setSearchFilters((prev: any) => ({
            ...prev,
            idPassportNumber: idPassportNumberFromUrl,
          }));

          // Track that this ID came from the URL so we can clear it on later navigation.
          injectedIdPassportNumberRef.current = idPassportNumberFromUrl;
        }

        // Load all dropdown data
        // Set options for dropdowns

        setMemberCategoryOptions(
          memberCategoriesData?.map((mc) => ({
            value: mc.id ?? '',
            label: mc.title ?? '',
          })) || [],
        );
        setMemberStatusOptions(
          memberStatusesData?.map((status) => ({
            value: status.id ?? '',
            label: status.title ?? '',
          })) || [],
        );
        setTerminationStatusOptions(
          terminationStatusesData?.map((status) => ({
            value: status.id ?? '',
            label: status.title ?? '',
          })) || [],
        );
        setRegistrationTypeOptions(
          registrationTypesData?.map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          })) || [],
        );
        setRegistrationStepOptions(
          registrationStepsData?.map((step) => ({
            value: step.id ?? '',
            label: step.title ?? '',
          })) || [],
        );
        setReferralTypeOptions(
          referralTypesData?.map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          })) || [],
        );
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    loadDropdownData();
  }, [statusFromUrl, idPassportNumberFromUrl, pageSize, memberStatusesData, memberCategoriesData, terminationStatusesData, registrationTypesData, registrationStepsData, referralTypesData]);

  // If we previously injected an ID from the URL, clear it when navigating to a members page
  // that no longer has the query param.
  useEffect(() => {
    if (hasIdPassportNumberFromUrl) return;

    const injected = injectedIdPassportNumberRef.current;
    if (!injected) return;

    const currentId = (searchFilters?.idPassportNumber ?? '').trim();
    // Only clear the UI field if it still matches what we injected.
    if (currentId === injected) {
      setSearchFilters((prev: any) => {
        const { idPassportNumber, ...rest } = prev ?? {};
        return rest;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIdPassportNumberFromUrl, location.pathname, location.search]);

  // If we have an ID/Passport in the URL, force page 1 (prevents paging mismatch)
  useEffect(() => {
    if (!hasIdPassportNumberFromUrl) return;
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPassportNumberFromUrl]);

  const previousFiltersRef2 = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef2.current !== currentUrlFiltersKey;

  // Fetch items when dependencies change (skip normal list when deep-linked via ID)
  useEffect(() => {
    // If we need status filtering but statusMap isn't ready yet, wait
    if (statusFromUrl && Object.keys(statusMap).length === 0) {
      return;
    }

    // Parse filters from URL to ensure we search with current URL params
    // This is the source of truth - not searchFilters state which may be stale
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);

    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef2.current) {
      return;
    }

    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    // This handles the case where user navigates between routes and searchFilters is stale
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef2.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef2.current = currentFiltersKey;
    }

    if (hasIdPassportNumberFromUrl) {
      fetchItems(page, size, {
        ...urlFilters,
        ...(statusFromUrl ? {} : { isDeleted: false }),
      });
      return;
    }

    if (statusFromUrl) {
      // Apply status filters for specific status views
      fetchItems(page, size, {
        ...urlFilters,
      });
    } else {
      // For "All", just apply user search filters + exclude deleted
      fetchItems(page, size, {
        ...urlFilters,
        isDeleted: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl, statusMap, hasIdPassportNumberFromUrl]);

  // Update the handleSearch function
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(
      1,
      pageSize,
      statusFromUrl ? { ...searchFilters } : { ...searchFilters, isDeleted: false },
    );
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: sortBy,
        sortDescending: sortDescending,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Members:', error);
    }
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, statusFromUrl ? {} : { isDeleted: false });
  };

  const handleDeleteOrArchive = async (dto: MemberDTO) => {
    try {
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting Member:', error);
    }
  };

  // Add state for dropdown options

  const [memberCategoryOptions, setMemberCategoryOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [memberStatusOptions, setMemberStatusOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);
  const [terminationStatusOptions, setTerminationStatusOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);
  const [registrationTypeOptions, setRegistrationTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [registrationStepOptions, setRegistrationStepOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [referralTypeOptions, setReferralTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // State for Change Status drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<MemberDTO>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const statusFieldName = 'memberStatusId';

  // Add boolean options
  const booleanOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  const handleDelete = async (member: MemberDTO) => {
    try {
      await remove(member.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  // Email handlers from useEmailHandlers hook
  const { handleSendEmail, handleSendEmailSubmit, handleViewRecordEmails } =
    useEmailHandlers<MemberDTO>({
      setSelectedItem: setSelectedMember,
      setDrawerTitle,
      setDrawerView,
      setIsDrawerOpen,
      sendEmail,
      fetchItems: async (page: number, size: number, filters: any) => {
        await fetchItems(page, size, filters);
      },
      currentPage,
      pageSize,
      searchFilters,
    });

  const handleArchive = async (member: MemberDTO) => {
    try {
      await archive(member.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error archiving member:', error);
    }
  };

  const handleRestore = async (member: MemberDTO) => {
    try {
      await restoreFromDeleted(member.id ?? '');
      await refreshAfterMutation();
      await showAlert({
        title: 'Success',
        text: 'Member restored successfully.',
        icon: 'success',
      });
    } catch (error: any) {
      console.error('Error restoring member:', error);

      // Extract error message from API response
      let errorMessage = 'An error occurred while restoring the member.';

      if (error?.response) {
        // error.response might be a JSON string, try to parse it
        try {
          const responseData = typeof error.response === 'string'
            ? JSON.parse(error.response)
            : error.response;

          if (responseData?.message) {
            errorMessage = responseData.message;
          }
        } catch (parseError) {
          // If parsing fails, try direct access
          if (error.response?.message) {
            errorMessage = error.response.message;
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      await showAlert({
        title: 'Cannot Restore Member',
        text: errorMessage,
        icon: 'error',
      });
    }
  };

  const handleRestoreFromArchive = async (member: MemberDTO) => {
    try {
      await restoreFromArchive(member.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring archived member:', error);
    }
  };

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which member IDs to update
      const idsToUpdate = selectedMemberIds.size > 0
        ? Array.from(selectedMemberIds)
        : [selectedMember.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        memberStatusId: data.memberStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
      };

      // Call the new bulk update endpoint
      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedMember({});
      setSelectedMemberIds(new Set()); // Clear selections
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedMember({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendProofOfPaymentLink = async (member: MemberDTO) => {
    try {
      await sendProofOfPaymentLink(member.id ?? '');
    } catch (error) {
      console.error('Error sending email proof upload:', error);
    }
  };

  const handleSendPasswordResetLink = async (member: MemberDTO) => {
    if (!member.email) {
      await showAlert({
        title: 'No Email Address',
        text: 'This member does not have an email address on file.',
        icon: 'warning',
      });
      return;
    }

    try {
      const response = await forgotPassword({ email: member.email });
      if (response?.success) {
        await showAlert({
          title: 'Success',
          text: 'Password reset link has been sent to the member\'s email address.',
          icon: 'success',
        });
      } else {
        await showAlert({
          title: 'Error',
          text: 'Failed to send password reset link. Please try again.',
          icon: 'error',
        });
      }
    } catch (error) {
      console.error('Error sending password reset link:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to send password reset link. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleCompleteEftPayment = async (member: MemberDTO) => {
    try {
      await processEftPayment({ componentId: member.id, entityType: 'Member' });
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error completing EFT payment:', error);
    }
  };

  const handleViewMandate = async (member: MemberDTO) => {
    try {
      const pdfUrl = await getMandatePdf(member.id ?? '');
      setViewingFile({ name: `Debit-Order-Mandate-${member.id}.pdf`, url: pdfUrl });
    } catch (error) {
      console.error('Error generating mandate PDF:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to generate debit order mandate. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleEditMandate = async (member: MemberDTO) => {
    try {
      const mandates = await getMandatesByMember(member.id ?? '');
      const latest = mandates?.data?.[0];
      if (!latest?.mandateId) {
        await showAlert({ title: 'Not Found', text: 'No mandate found for this member.', icon: 'warning', confirmButtonText: 'OK' });
        return;
      }
      setEditingMandateId(latest.mandateId);
    } catch {
      await showAlert({ title: 'Error', text: 'Failed to load mandate. Please try again.', icon: 'error', confirmButtonText: 'OK' });
    }
  };

  const handleOpenDrawer = async (
    member: MemberDTO,
    drawerView: MemberDrawerViews,
    drawerTitle: string,
    drawerSize: "small" | "large" = "large"
  ) => {
    setSelectedMember(member);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    setIsDrawerOpen(true);
    // If opening from action menu (not bulk update), clear checkbox selections
    // to avoid confusion between single and bulk operations
    if (drawerView === DrawerViewConstants.FEEDBACK && selectedMemberIds.size === 0) {
      // This is a single-member action from the menu
      // No change needed to selections
    } else if (drawerView === DrawerViewConstants.FEEDBACK && !selectedMemberIds.has(member.id ?? '')) {
      // User clicked action menu on a member that's not in the selection
      // Clear selections to prevent accidental bulk update
      setSelectedMemberIds(new Set());
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedMemberIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = items.map(item => item.id).filter((id): id is string => !!id);
      setSelectedMemberIds(new Set(allIds));
    } else {
      setSelectedMemberIds(new Set());
    }
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedMemberIds)[0];
    const firstMember = items.find(m => m.id === firstSelectedId) || {};
    const count = selectedMemberIds.size;
    handleOpenDrawer(
      firstMember as MemberDTO,
      DrawerViewConstants.FEEDBACK,
      `Update Status (${count} member${count > 1 ? 's' : ''})`
    );
  };

  const handleBulkArchive = async () => {
    const count = selectedMemberIds.size;
    const confirmed = await confirmAction({
      title: 'Archive Members',
      text: `Are you sure you want to archive ${count} member${count > 1 ? 's' : ''}?`,
      confirmButtonText: 'Archive',
      icon: 'warning'
    });

    if (!confirmed) return;

    try {
      const recordIds = Array.from(selectedMemberIds);
      await bulkArchive(recordIds);
      setSelectedMemberIds(new Set());
      setShowBulkActions(false);
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error archiving members:', error);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedMemberIds.size;
    const confirmed = await confirmAction({
      title: 'Delete Members',
      text: `Are you sure you want to delete ${count} member${count > 1 ? 's' : ''}? This action cannot be undone.`,
      confirmButtonText: 'Delete',
      icon: 'warning'
    });

    if (!confirmed) return;

    try {
      const recordIds = Array.from(selectedMemberIds);
      await bulkDelete(recordIds);
      setSelectedMemberIds(new Set());
      setShowBulkActions(false);
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting members:', error);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setDrawerSize("large");
    setSelectedMember({});
    setShowBulkActions(false);
  };

  // Close bulk actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    if (showBulkActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBulkActions]);

  const handleSort = (sortKey: string) => {
    // If clicking the same column, toggle direction
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      // New column, default to descending
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);

    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);

    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={statusFromUrl ? `/admin/members/status/${statusFromUrl}` : '/admin/members'}
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Members', href: '#' },
        ...(statusFromUrl
          ? [
            {
              label: getStatusDisplayName(statusFromUrl),
              href: '#',
            },
          ]
          : []),
      ]}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Members.View"
    >
      <Card
        title={`Manage Members ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {selectedMemberIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedMemberIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkStatusUpdate();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Update Status
                    </button>
                    <button
                      onClick={() => {
                        handleBulkArchive();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Archive Selected
                    </button>
                    <button
                      onClick={() => {
                        handleBulkDelete();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-lg"
                    >
                      Delete Selected
                    </button>
                  </div>
                )}
              </div>
            )}
            <Button variant="secondary" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Basic Search' : 'Advanced Search'}
            </Button>
            <Button variant="secondary" onClick={handleExport}>
              Export
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">

          {/* Advanced Search Fields */}
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firstName: e.target.value,
                  })
                }
                placeholder="Search by First Name"
              />
              <FormField
                id="lastName"
                label="Search by Last Name"
                value={searchFilters.lastName || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    lastName: e.target.value,
                  })
                }
                placeholder="Search by Last Name"
              />
              <FormField
                id="idPassportNumber"
                label="Search by ID/Passport Number"
                value={searchFilters.idPassportNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    idPassportNumber: e.target.value,
                  })
                }
                placeholder="Search by ID/Passport Number"
              />
              <FormField
                id="memberTypeId"
                label="Search by Member Type"
                type="dropdown"
                value={searchFilters.memberTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberTypeId: e.target.value,
                  })
                }
                placeholder="Select Member Type"
                options={(memberTypesData || []).map((mt) => ({ value: mt.id ?? '', label: mt.title ?? '' }))}
                isLoading={isLoadingMemberTypes}
              />
              <FormField
                id="memberStatusId"
                label="Search by Member Status"
                type="dropdown"
                value={searchFilters.memberStatusId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberStatusId: e.target.value,
                  })
                }
                placeholder="Select Member Status"
                options={memberStatusOptions}
                isLoading={isLoadingMemberStatuses}
              />
              <FormField
                id="memberCategoryId"
                label="Search by Member Category"
                type="dropdown"
                value={searchFilters.memberCategoryId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberCategoryId: e.target.value,
                  })
                }
                placeholder="Select Member Category"
                options={memberCategoryOptions}
                isLoading={isLoadingMemberCategories}
              />
              <FormField
                id="memberIdTypeId"
                label="Search by Member Id Type"
                type="dropdown"
                value={searchFilters.memberIdTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberIdTypeId: e.target.value,
                  })
                }
                placeholder="Select Member Id Type"
                options={(memberIdTypesData || []).map((mit) => ({
                  value: mit.id ?? '',
                  label: mit.title ?? '',
                }))}
                isLoading={isLoadingMemberIdTypes}
              />
              <FormField
                id="languageId"
                label="Search by Language"
                type="dropdown"
                value={searchFilters.languageId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    languageId: e.target.value,
                  })
                }
                placeholder="Select Language"
                options={(languages || []).map((l) => ({
                  value: l.id ?? '',
                  label: l.title ?? '',
                }))}
                isLoading={isLoadingLanguages}
              />
              <FormField
                id="countryId"
                label="Search by Country"
                type="dropdown"
                value={searchFilters.countryId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    countryId: e.target.value,
                  })
                }
                placeholder="Select Country"
                options={(countries || []).map((country) => ({ value: country.id ?? '', label: country.title ?? '' }))}
                isLoading={isLoadingCountries}
              />
              <FormField
                id="provinceId"
                label="Search by Province"
                type="dropdown"
                value={searchFilters.provinceId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    provinceId: e.target.value,
                  })
                }
                placeholder="Select Province"
                options={(provinces || []).map((p) => ({
                  value: p.id ?? '',
                  label: p.title ?? '',
                }))}
                isLoading={isLoadingProvinces}
              />
              <FormField
                id="memberDisciplinaryStatusId"
                label="Search by Disciplinary Status"
                type="dropdown"
                value={searchFilters.memberDisciplinaryStatusId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberDisciplinaryStatusId: e.target.value,
                  })
                }
                placeholder="Select Disciplinary Status"
                options={(disciplinaryStatusesData || []).map((status) => ({
                  value: status.id ?? '',
                  label: status.title ?? '',
                }))}
                isLoading={isLoadingDisciplinaryStatuses}
              />
              <FormField
                id="memberTerminationStatusId"
                label="Search by Termination Status"
                type="dropdown"
                value={searchFilters.memberTerminationStatusId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberTerminationStatusId: e.target.value,
                  })
                }
                placeholder="Select Termination Status"
                options={terminationStatusOptions}
                isLoading={isLoadingTerminationStatuses}
              />
              <FormField
                id="memberRegistrationTypeId"
                label="Search by Registration Type"
                type="dropdown"
                value={searchFilters.memberRegistrationTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberRegistrationTypeId: e.target.value,
                  })
                }
                placeholder="Select Registration Type"
                options={registrationTypeOptions}
                isLoading={isLoadingRegistrationTypes}
              />
              <FormField
                id="memberRegistrationStepId"
                label="Search by Registration Step"
                type="dropdown"
                value={searchFilters.memberRegistrationStepId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberRegistrationStepId: e.target.value,
                  })
                }
                placeholder="Select Registration Step"
                options={registrationStepOptions}
                isLoading={isLoadingRegistrationSteps}
              />
              <FormField
                id="memberReferralTypeId"
                label="Search by Referral Type"
                type="dropdown"
                value={searchFilters.memberReferralTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    memberReferralTypeId: e.target.value,
                  })
                }
                placeholder="Select Referral Type"
                options={referralTypeOptions}
                isLoading={isLoadingReferralTypes}
              />
              <FormField
                id="isDedicatedStatusLapsed"
                label="Search by Dedicated Status Lapsed"
                type="dropdown"
                value={searchFilters.isDedicatedStatusLapsed || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    isDedicatedStatusLapsed: e.target.value,
                  })
                }
                placeholder="Select Dedicated Status Lapsed"
                options={booleanOptions}
              />
              <FormField
                id="isDebitOrderPayment"
                label="Search by Debit Order Members"
                type="dropdown"
                value={searchFilters.isDebitOrderPayment || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    isDebitOrderPayment: e.target.value,
                  })
                }
                placeholder="Select Debit Order Payment"
                options={booleanOptions}
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex  justify-between items-center">
            <div className="flex space-x-4 mt-4">
              {showAdvanced && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}
            </div>

            {!showAdvanced && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}
          </div>
        </form>

        <MemberTable
          data={areFiltersStale ? [] : items}
          statusFromUrl={statusFromUrl}
          handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
          handleEdit={(dto) => navigate(`/admin/members/${dto.id}`)}
          handleDelete={handleDelete}
          handleSendEmail={handleSendEmail}
          handleViewRecordEmails={handleViewRecordEmails}
          handleArchive={handleArchive}
          handleRestore={handleRestore}
          handleRestoreFromArchive={handleRestoreFromArchive}
          handleImpersonateMemberLogin={(dto) => impersonate(dto.id, dto.memberStatusId)}
          handleChangeStatus={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Add Note')
          }
          handleAddNote={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Notes History')
          }
          handleViewNoteHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Change Status')
          }
          handleViewInvoiceHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_INVOICE_HISTORY, 'Invoice History')
          }
          handleSendProofOfPaymentLink={handleSendProofOfPaymentLink}
          handleSendPasswordResetLink={handleSendPasswordResetLink}
          handleCompleteEftPayment={handleCompleteEftPayment}
          handleViewMandate={handleViewMandate}
          handleEditMandate={handleEditMandate}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
          selectedIds={selectedMemberIds}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
        />
      </Card>

      <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} title={drawerTitle} size={drawerSize}>

        {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
          <MemberQuickDetails memberId={selectedMember?.id ?? ''} onClose={handleDrawerClose} />
        )}

        {drawerView === DrawerViewConstants.FEEDBACK && (
          <FeedbackForm
            componentId={ComponentConstants.MEMBERS}
            recordId={selectedMember.id ?? ''}
            componentStatuses={memberStatusOptions}
            statusFieldName={statusFieldName}
            initialData={selectedMember}
            onSubmit={handleStatusSubmit}
            onCancel={handleDrawerClose}
            updateLoading={isSubmitting}
          />
        )}

        {drawerView === DrawerViewConstants.ADD_NOTE && (
          <AddNote
            memberId={selectedMember.id || ''}
            componentId={ComponentConstants.MEMBERS}
            recordId={selectedMember.id || ''}
            onSubmit={handleAddNote}
            onCancel={handleDrawerClose}
            isLoading={isSubmitting}
          />
        )}

        {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && <ViewNoteHistory memberId={selectedMember.id || ''} />}
        {drawerView === DrawerViewConstants.VIEW_INVOICE_HISTORY && <ViewInvoiceHistory memberId={selectedMember.id || ''} />}

        <EmailDrawerContent
          drawerView={drawerView}
          selectedItem={selectedMember}
          componentId={ComponentConstants.MEMBERS}
          getMemberId={(dto) => dto?.id || ''}
          onEmailSubmit={handleSendEmailSubmit}
          onClose={handleDrawerClose}
          isSending={isSendingEmail}
        />
      </Drawer>

      {
        viewingFile && (
          <FileViewer
            sasUrl={viewingFile.url}
            fileName={viewingFile.name}
            onClose={() => setViewingFile(null)}
          />
        )
      }

      <EditMandateDrawer
        isOpen={!!editingMandateId}
        onClose={() => setEditingMandateId(null)}
        mandateId={editingMandateId ?? ''}
        onSuccess={refreshAfterMutation}
      />
    </AdminLayout >
  );
};

export default MemberList;
